import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Button,
  Image,
  utils,
  Modal,
  RATIO_16_9,
  ReservationSummary,
  Spacer,
  TranslationProvider,
  WIDTH_800,
  getTranslation,
} from '@resy/ui-kitchen';

import Error from '../Error';
import InfoBar from '../InfoBar';
import keyAboveThreshhold from '../../utils/keyAboveThreshhold';
import Menu from '../Menu';
import MenuType from '../../types/Menu';
import modalStyles from '../../constants/modalStyles';
import OnScrollView from '../OnScrollView';
import OPENAI_WIDGET from '../../constants/srcTypes';

import {
  clearCategory,
  getAddOn,
  getAddOnsWithQuantity,
  getFirstUnfulfilledCategory,
  getUpdatedItemsForBooking,
  updateBookingInfoWithAddOnsTokens,
  updateBookingInfoForSoldOut,
  validateCheckout,
} from '../../utils/addOnsHelpers';
import WidgetNav from '../WidgetNav';
import WidgetPage, { WidgetPageBody, WidgetPageFooter, WidgetPageHeader } from '../WidgetPage';

import './AddOnPage.scss';

const addOnPagePropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  backPath: PropTypes.string,
  clearError: PropTypes.func,
  errors: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        message: PropTypes.string,
      }),
    ])
  ),
  imageServiceConfig: PropTypes.shape({
    baseRequestUrl: PropTypes.string,
  }),
  items: PropTypes.arrayOf(PropTypes.shape({
    category_id: PropTypes.number,
    item_token: PropTypes.string,
    item_id: PropTypes.string,
    name: PropTypes.string,
    quantity: PropTypes.number,
  })),
  detail: PropTypes.shape({
    city: PropTypes.string,
    currencyCode: PropTypes.string,
    description: PropTypes.string,
    eventName: PropTypes.string,
    imageDimensions: PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.number
      )
    ),
    // [image id] : [image ratio eg 1:1] : [image width eg 200]: image url
    imageUrls: PropTypes.shape({
      [PropTypes.string]: PropTypes.shape({
        [PropTypes.string]: PropTypes.shape({
          [PropTypes.string]: PropTypes.string,
        }),
      }),
    }),
    isEvent: PropTypes.bool,
    isGda: PropTypes.bool,
    itemName: PropTypes.string,
    itemMenu: PropTypes.string,
    itemDescription: PropTypes.string,
    price: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    rawImageNames: PropTypes.arrayOf(
      PropTypes.string
    ),
    seats: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    venueId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    venue: PropTypes.string,
  }),
  menu: MenuType,
  navigate: PropTypes.func.isRequired,
  navSearch: PropTypes.shape({
    date: PropTypes.string,
    seats: PropTypes.number,
  }).isRequired,
  setItems: PropTypes.func.isRequired,
  sourceId: PropTypes.string,
  viewPortHeight: PropTypes.number,
};

class AddOnPage extends React.Component {
  static propTypes = addOnPagePropTypes;

  static defaultProps = {
    backPath: undefined,
    clearError: undefined,
    detail: undefined,
    errors: undefined,
    imageServiceConfig: undefined,
    items: [],
    menu: undefined,
    viewPortHeight: undefined,
    sourceId: undefined,
  }

  static updateBookingInfoWithAddOnsTokens = updateBookingInfoWithAddOnsTokens

  static updateBookingInfoForSoldOut = updateBookingInfoForSoldOut

  state = {
    // used to voice to the user change in items
    assistiveDesc: '',
    // used to scroll into view
    categoryId: undefined,
    // for visual enhancement
    footerShadow: true,
    // used to retrigger voiceover alert if subtotal hasn't changed
    numClicks: 0,
    // for visual enhancement
    titleOpacity: 0,
  }

  componentDidMount() {
    const { items, menu, setItems } = this.props;
    if (menu && setItems) {
      AddOnPage.updateBookingInfoWithAddOnsTokens(items, setItems, menu);
      AddOnPage.updateBookingInfoForSoldOut(items, setItems, menu);
    }
  }

  componentDidUpdate(prevProps) {
    const { items, menu, setItems } = this.props;
    const { menu: prevMenu } = prevProps;
    const { categoryId } = this.state;

    if (menu !== prevMenu) {
      AddOnPage.updateBookingInfoWithAddOnsTokens(items, setItems, menu);
      AddOnPage.updateBookingInfoForSoldOut(items, setItems, menu);
    }

    if (categoryId) {
      // by convention catgory div will have an id `category-${categoryId}`
      // const getElementById = document?.getElementById || (() => {});
      const el = document && document.getElementById(`category-${categoryId}`);
      /* eslint-disable-next-line no-unused-expressions */
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      /* eslint-disable-next-line react/no-did-update-set-state */
      this.setState({ categoryId: undefined });
    }
  }

  clearCategory = (categoryId) => {
    const { items, setItems } = this.props;
    clearCategory(categoryId, items, setItems);
  }

  getAddOn = (id, categoryId) => {
    const { menu } = this.props;

    // eslint-disable-next-line consistent-return
    return getAddOn(menu, id, categoryId);
  }

  getAddOnsWithQuantity = () => {
    const { items, menu } = this.props;

    return getAddOnsWithQuantity(
      menu,
      items || []
    );
  }

  getHeroImage = () => {
    const {
      detail: {
        imageDimensions,
        imageUrls,
        rawImageNames,
      } = {},
    } = this.props;

    const threshold = 700;

    if (imageUrls) {
      const suitableImageId = keyAboveThreshhold(imageUrls, threshold);
      if (suitableImageId) {
        return suitableImageId;
      }
    }

    if (!rawImageNames) {
      return undefined;
    }

    const suitable = imageDimensions.findIndex(
      (arr) => arr[0] > threshold
    );

    if (suitable === -1) {
      return undefined;
    }

    return rawImageNames[0];
  }

  getScrollEvents = () => [
    {
      selector: ['.WidgetPageHeader'],
      func: this.scrollFunc,
    },
    {
      selector: ['.AddOnPage__ScrollView'],
      func: this.scrollFuncFooter,
    },
  ]

  getScrollVals = () => {
    const {
      detail: {
        eventName,
        venue,
      } = {},
    } = this.props;

    return {
      eventName,
      venue,
    };
  }

  getItemsSubtotal = () => {
    const { items: structItems } = this.props;
    if (!structItems) {
      return 0;
    }
    return structItems.reduce(
      (iter, val) => {
        const item = this.getAddOn(val.item_id, val.category_id);
        try {
          return iter + item.price * val.quantity;
        } catch {
          return iter;
        }
      },
      0
    );
  }

  getSubtotal = () => {
    const { detail: { price, seats } } = this.props;

    return this.getItemsSubtotal() + ((price || 0) * seats);
  }

  getChangeDescription = (
    menu,
    itemId,
    items,
    categoryId,
    quantity
  ) => {
    const addOn = getAddOn(menu, itemId, categoryId);
    if (!addOn) {
      return '';
    }
    const {
      name,
    } = addOn;
    const currQuantity = items?.find(
      (item) => item.item_id === itemId && item.category_id === categoryId
    )?.quantity;
    const cardinal = quantity > (currQuantity || 0) ? 'Increment' : 'Decrement';
    return `${cardinal} quantity of ${name} to ${quantity}`;
  }

  onAddItem = (itemId, categoryId, quantity) => {
    const {
      items, menu, setItems,
    } = this.props;

    const assistiveDesc = this.getChangeDescription(
      menu,
      itemId,
      items,
      categoryId,
      quantity
    );

    const updated = getUpdatedItemsForBooking({
      menu,
      structItems: items || [],
      itemId,
      categoryId,
      quantity,
    });

    setItems(updated);

    this.setState({ assistiveDesc });
  }

  onCheckout = () => {
    const {
      navigate,
    } = this.props;

    if (!this.validateMenu()) {
      return;
    }

    navigate('/reservation-details');
  }

  onCloseModal = () => {
    const { clearError } = this.props;
    if (clearError) {
      clearError();
    }
  }

  scrollFunc = (elems, scrollTop) => {
    const diffWithScrollTop = (elems[0].offsetTop + elems[0].offsetHeight) - scrollTop;
    const perc = 1 - diffWithScrollTop / elems[0].offsetHeight;

    this.setState({
      titleOpacity: (perc <= 0) ? 0 : 1,
    });
  }

  scrollFuncFooter = (elems, scrollTop) => {
    this.setState({
      footerShadow: !((scrollTop + elems[0].clientHeight) >= (elems[0].scrollHeight - 60)),
    });
  }

  renderCheckout = () => {
    const {
      detail: {
        isGda,
      } = {},
    } = this.props;

    return (
      <div className="AddOnPage__checkout">
        {this.renderSubtotal()}
        <Button
          data-test-id="add_on_page-button-checkout"
          disabled={!validateCheckout(this.getAddOnsWithQuantity())}
          lg
          onClick={this.onCheckout}
          variant={isGda ? 'rga' : 'primary'}
        >
          <TranslationProvider translationKey="checkout">
            <span>Checkout</span>
          </TranslationProvider>
        </Button>
      </div>
    );
  }

  renderInfoBar = () => {
    const {
      detail: {
        seats,
        timeslot,
      } = {},
    } = this.props;

    return (
      <InfoBar seats={seats} timeslot={timeslot} />
    );
  }

  renderHero = () => {
    const {
      imageServiceConfig: {
        baseRequestUrl: baseUrl,
      } = {},
      detail: {
        venueId,
      } = {},
    } = this.props;

    const img = this.getHeroImage();

    if (!img) {
      return null;
    }

    const props = {
      baseUrl,
      imageId: img,
      ratio: RATIO_16_9,
      venueId,
      width: WIDTH_800,
    };

    return <Image {...props} />;
  }

  renderMenu = () => {
    const {
      detail: {
        currencyCode,
        seats,
      } = {},
      items,
      menu,
      sourceId,
    } = this.props;

    if (!menu) {
      return null;
    }

    return (
      <Menu
        clearCategory={this.clearCategory}
        currencyCode={currencyCode}
        items={items || []}
        menu={this.getAddOnsWithQuantity()}
        onChange={this.onAddItem}
        seats={seats}
        sourceId={sourceId}
      />
    );
  }

  renderModal = () => {
    const { errors } = this.props;

    if (!errors?.length) {
      return null;
    }
    return (
      <Modal isOpen={!!errors.length} contentLabel="Example Modal" style={modalStyles}>
        <Error onClick={this.onCloseModal} error={errors[0]} />
      </Modal>
    );
  }

  renderSubtotal = () => {
    const { detail } = this.props;
    if (!detail) {
      return null;
    }

    const {
      currencyCode,
    } = detail;

    const subtotal = this.getSubtotal();

    const { assistiveDesc, numClicks } = this.state;

    return (
      <div className="AddOnPage__subtotal">
        <TranslationProvider translationKey="pickup_price_subtotal">
          <span className="AddOnPage__subtotalText">Subtotal:</span>
        </TranslationProvider>
        <div key={`${subtotal}_${numClicks}`} aria-label={assistiveDesc} className="AddOnPage__subtotalValue" aria-live="assertive">{utils.getFormattedPrice(subtotal, currencyCode)}</div>
      </div>
    );
  }

  renderWidgetHeader = (headerTopSpace) => {
    const {
      detail,
    } = this.props;

    const hasHero = this.getHeroImage();

    return (
      <>
        {!hasHero && <Spacer height={headerTopSpace} />}
        <WidgetPageHeader className={hasHero && 'WidgetPageHeaderWithHero'}>
          {this.renderHero()}
          <ReservationSummary {...detail} />
        </WidgetPageHeader>
      </>
    );
  }

  renderWidgetNav = () => {
    const {
      Analytics,
      backPath,
      navigate,
      navSearch,
    } = this.props;

    const hasHero = this.getHeroImage();
    const { eventName, venue } = this.getScrollVals();
    const { titleOpacity } = this.state;
    return (
      <WidgetNav
        Analytics={Analytics}
        backPath={backPath}
        invert={!!hasHero}
        navigate={navigate}
        pageContext="add-ons"
        search={navSearch}
        showBack
        showProfileMenu
        title={eventName || venue}
        titleOpacity={titleOpacity}
        venueName={venue}
      />
    );
  }

  validateMenu = () => {
    const {
      detail: {
        seats,
      } = {},
      items,
      menu,
    } = this.props;

    const { numClicks } = this.state;

    const cat = getFirstUnfulfilledCategory(
      menu,
      items || [],
      seats
    );

    if (!cat) {
      // valid
      return true;
    }

    this.setState({
      assistiveDesc: `Please correct your selection for the category ${cat.name}`,
      /* eslint-disable-next-line camelcase */
      categoryId: cat?.option_group_id,
      numClicks: numClicks + 1,
    });

    return false;
  }

  render() {
    const {
      footerShadow,
    } = this.state;

    const {
      viewPortHeight,
      sourceId,
    } = this.props;

    const bodyBottomSpace = sourceId === OPENAI_WIDGET ? '60' : '20';
    const headerTopSpace = sourceId === OPENAI_WIDGET ? '20' : '60';

    return (
      <WidgetPage className="AddOnPage" height={viewPortHeight} ariaLabel={getTranslation({ translationKey: 'checkout', fallback: 'Checkout' })}>
        {this.renderModal()}
        {this.renderInfoBar()}
        {this.renderWidgetNav()}
        <OnScrollView className="AddOnPage__ScrollView" scrollEvents={this.getScrollEvents()}>
          {this.renderWidgetHeader(headerTopSpace)}
          <WidgetPageBody>
            {this.renderMenu()}
            <Spacer height={bodyBottomSpace} />
          </WidgetPageBody>
          <WidgetPageFooter shadow={footerShadow}>
            {this.renderCheckout()}
          </WidgetPageFooter>
        </OnScrollView>
      </WidgetPage>
    );
  }
}

const mapStateToProps = ({ resyCore, resyRouter }) => ({
  imageServiceConfig: resyCore.serviceConfigMap?.imageService,
  navSearch: resyRouter.search,
  sourceId: resyRouter?.sourceId,
});

export { addOnPagePropTypes, AddOnPage as ComponentUnderTest };
export default connect(mapStateToProps)(AddOnPage);
